import { NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

import { AuthService } from '../core/auth/auth.service';
import { PermissionService } from '../core/auth/permission.service';
import { AgentPresenceService } from '../core/realtime/agent-presence.service';
import { ConnectionStatusService } from '../core/realtime/connection-status.service';
import { ConversationsHubService } from '../core/realtime/conversations-hub.service';
import { PresenceHubService } from '../core/realtime/presence-hub.service';
import { TicketingHubService } from '../core/realtime/ticketing-hub.service';
import { ThemeService } from '../core/preferences/theme.service';
import { UserPreferencesService } from '../core/preferences/user-preferences.service';
import { AppLanguage, TranslationService } from '../core/i18n/translation.service';
import { TranslatePipe } from '../core/i18n/translate.pipe';
import { HasPermissionDirective } from '../shared/directives/has-permission.directive';
import { GlobalSearchComponent } from '../shared/components/global-search/global-search.component';
import { SoundSettingsComponent } from '../shared/components/sound-settings/sound-settings.component';
import { NotificationBellComponent } from '../features/notifications/bell/notification-bell.component';

/**
 * The application shell: topbar + sidebar + router-outlet. Built on PrimeNG's Aura preset
 * plus Tailwind, not PrimeTek's commercial "Poseidon" admin template -- that is a separate
 * paid product, not a `theme` preset, and isn't part of this project's dependencies (see
 * the implementation report's UX Decisions section). The sidebar's nav items are
 * permission-filtered via *appHasPermission. MessageService is provided here (not root --
 * PrimeNG's own convention, since it is a plain injectable, not tree-shakable) so
 * NotificationBellComponent's toasts render in this shell's single <p-toast>, no matter
 * which routed page is active underneath it.
 */
@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ToolbarModule,
    ButtonModule,
    SelectModule,
    ToastModule,
    TooltipModule,
    HasPermissionDirective,
    GlobalSearchComponent,
    NotificationBellComponent,
    SoundSettingsComponent,
    TranslatePipe,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
  providers: [MessageService],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly preferences = inject(UserPreferencesService);
  private readonly translationService = inject(TranslationService);
  private readonly connectionStatusService = inject(ConnectionStatusService);
  private readonly ticketingHub = inject(TicketingHubService);
  private readonly conversationsHub = inject(ConversationsHubService);
  private readonly presenceHub = inject(PresenceHubService);
  private readonly agentPresence = inject(AgentPresenceService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly subscriptions = new Subscription();

  readonly isDarkMode = this.preferences.darkMode;
  readonly connectionStatus = this.connectionStatusService.status;
  readonly language = this.preferences.language;
  readonly isRtl = this.translationService.isRtl;

  readonly languageOptions: { label: string; value: AppLanguage }[] = [
    { label: 'English', value: 'en' },
    { label: 'العربية', value: 'ar' },
  ];

  readonly presenceOptions: { label: string; value: 'Online' | 'Busy' | 'Away' }[] = [
    { label: 'Online', value: 'Online' },
    { label: 'Busy', value: 'Busy' },
    { label: 'Away', value: 'Away' },
  ];

  get myPresenceStatus(): 'Online' | 'Busy' | 'Away' {
    const status = this.agentPresence.statusFor(this.permissionService.userId());
    return status === 'Offline' ? 'Online' : status;
  }

  /** Mobile responsiveness: below the `md` breakpoint the icon rail is hidden and this
   * drives a slide-in overlay drawer instead, opened via a fixed bottom menu button. */
  readonly sidebarOpen = signal(false);

  ngOnInit(): void {
    // Global (app-shell-level, not page-level) toasts for New Ticket / New Message
    // Notifications (Stream 8) -- an agent sees these regardless of which page they're on,
    // unlike the per-page hub subscriptions in the board/thread/inbox pages, which only
    // refresh that page's own list.
    void this.ticketingHub.start();
    void this.conversationsHub.start();
    void this.presenceHub.start().then(() => this.agentPresence.initialize());

    this.subscriptions.add(
      this.ticketingHub.ticketCreated$.subscribe((event) =>
        this.messageService.add({ severity: 'info', summary: 'New ticket', detail: `#${event.ticketNumber} · ${event.subject}` }),
      ),
    );

    this.subscriptions.add(
      this.conversationsHub.messageReceived$.subscribe((event) =>
        this.messageService.add({ severity: 'info', summary: 'New message', detail: event.preview }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  setLanguage(language: AppLanguage): void {
    this.translationService.setLanguage(language);
  }

  setMyPresence(status: 'Online' | 'Busy' | 'Away'): void {
    void this.presenceHub.setPresence(status);
  }

  signOut(): void {
    this.authService.clearToken();
    void this.router.navigateByUrl('/login');
  }
}
